import type { IntakeSpec } from '@shared/types';
import type { AppLocale } from '@/i18n/locales';

const LABELS = {
  en: {
    category: 'Category',
    use: 'Use',
    size: 'Size',
    material: 'Material',
    fastening: 'Fastening',
    quality: 'Quality level',
    drawing: 'Drawing',
    section: 'section',
    tolerance: 'tolerance',
    jisStrict: 'JIS strict',
    unspecified: 'unspecified',
    none: 'none',
    yes: 'yes',
    no: 'no',
    categories: {
      furniture: 'Furniture / Woodwork',
      mechanical: 'Mechanical parts',
      architecture: 'Architecture models',
      printing: '3D printing',
    },
    qualities: {
      draft: 'Draft',
      standard: 'Standard',
      manufacturing: 'Manufacturing',
      extreme: 'Extreme',
    },
  },
  ja: {
    category: 'カテゴリ',
    use: '用途',
    size: '寸法',
    material: '素材',
    fastening: '接合',
    quality: '品質レベル',
    drawing: '図面',
    section: '断面',
    tolerance: '公差',
    jisStrict: 'JIS厳格',
    unspecified: '指定なし',
    none: 'なし',
    yes: 'あり',
    no: 'なし',
    categories: {
      furniture: '家具・木工',
      mechanical: '機械部品',
      architecture: '建築模型',
      printing: '3Dプリント',
    },
    qualities: {
      draft: '下書き',
      standard: '標準',
      manufacturing: '製造',
      extreme: '極限（製造グレード以上）',
    },
  },
  ar: {
    category: 'الفئة',
    use: 'الاستخدام',
    size: 'الأبعاد',
    material: 'المادة',
    fastening: 'التثبيت',
    quality: 'مستوى الجودة',
    drawing: 'الرسم',
    section: 'المقاطع',
    tolerance: 'التفاوت',
    jisStrict: 'JIS صارم',
    unspecified: 'غير محدد',
    none: 'لا يوجد',
    yes: 'نعم',
    no: 'لا',
    categories: {
      furniture: 'أثاث / نجارة',
      mechanical: 'أجزاء ميكانيكية',
      architecture: 'نماذج معمارية',
      printing: 'طباعة ثلاثية الأبعاد',
    },
    qualities: {
      draft: 'مسودة',
      standard: 'قياسي',
      manufacturing: 'تصنيعي',
      extreme: 'أقصى تفصيل',
    },
  },
};

function formatSize(size: IntakeSpec['core']['size'], locale: AppLocale): string {
  if (!size) return LABELS[locale].unspecified;
  const parts = [size.length, size.width, size.height]
    .map((n) => (n != null ? n.toString() : '?'))
    .join(' × ');
  return `${parts} mm`;
}

function formatDetailsBlock(details: IntakeSpec['details'], locale: AppLocale): string {
  // Render every key/value pair (except category/subType which are in the header)
  // as a short label/value line. Keep it terse — this is preface, not documentation.
  const lines: string[] = [];
  for (const [k, v] of Object.entries(details)) {
    if (k === 'category' || k === 'subType') continue;
    lines.push(`${fieldLabel(k, locale)}: ${formatValue(v, locale)}`);
  }
  return lines.join('\n');
}

function fieldLabel(key: string, locale: AppLocale): string {
  if (locale === 'ja') return `【${key}】`;
  return key;
}

function formatValue(v: unknown, locale: AppLocale): string {
  if (typeof v === 'boolean') return v ? LABELS[locale].yes : LABELS[locale].no;
  if (Array.isArray(v)) return v.length === 0 ? LABELS[locale].none : v.join(', ');
  if (v && typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function line(label: string, value: string, locale: AppLocale): string {
  return locale === 'ja' ? `【${label}】${value}` : `${label}: ${value}`;
}

export function humanReadableSummary(
  intake: IntakeSpec,
  locale: AppLocale = 'ja',
): string {
  const l = LABELS[locale];
  const lines: string[] = [];

  lines.push(line(l.category, `${l.categories[intake.category]} -> ${intake.subType}`, locale));
  lines.push(line(l.use, intake.core.intendedUse, locale));
  lines.push(line(l.size, formatSize(intake.core.size, locale), locale));
  if (intake.core.primaryMaterial) lines.push(line(l.material, intake.core.primaryMaterial, locale));
  if (intake.core.fastenerPreference) lines.push(line(l.fastening, intake.core.fastenerPreference, locale));
  lines.push(line(l.quality, l.qualities[intake.qualityLevel], locale));
  lines.push('');
  lines.push(formatDetailsBlock(intake.details, locale));
  lines.push('');
  lines.push(
    line(
      l.drawing,
      `${intake.drawing.scope} / ${l.section} ${intake.drawing.sectionCuts}` +
        ` / ${l.tolerance} ${intake.drawing.toleranceClass}` +
        (intake.drawing.jisStrict ? ` / ${l.jisStrict}` : ''),
      locale,
    ),
  );

  if (intake.freeform.trim()) {
    lines.push('');
    lines.push(intake.freeform.trim());
  }

  return lines.join('\n');
}
